import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Form, Row, Col, Button, FormCheck } from "react-bootstrap";
import ReactSelect from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

function ReportDelegationModal(props) {
  const {
    action = "Add",
    handleSubmit,
    show,
    onHide,
    loading = false,
    Year_ID = "",
    Vote_ID = "",
    LG_Vote_ID = "",
    user,
    accountingOfficerOptions = [],
    altAccountingOfficerOptions = [],
    StatusOptions = [],
    delegationData = {},
    isLG = false,
  } = props;

  // Use ref to track if this is the initial mount
  const isInitialMount = useRef(true);
  const prevShowRef = useRef(show);
  const prevDelegationDataRef = useRef(delegationData);

  // local loading state to prevent double submission
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initial form state
  const initialFormData = {
    ReasonForDelegation: "",
    StartDate: null,
    EndDate: null,
    isConsented: false,
    Accounting_Officer_ID: "",
    Alt_Accounting_Officer_ID: "",
    Status: "Active",
    Remarks: "",
  };

  // Form state
  const [formData, setFormData] = useState(initialFormData);
  const [selectedAO, setSelectedAO] = useState(null);
  const [selectedAltAO, setSelectedAltAO] = useState(null);

  const isCreateMode = action === "Add";
  const isUpdateMode = action === "Update";

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleDateChange = (date, fieldName) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: date,
    }));
  };

  const handleAltAOChange = (selectedOption) => {
    setSelectedAltAO(selectedOption);
    setFormData((prev) => ({
      ...prev,
      Alt_Accounting_Officer_ID: selectedOption?.value || "",
    }));
  };

  // Helper function to load delegation data
  const loadDelegationData = useCallback(() => {
    if (
      !show ||
      !isUpdateMode ||
      !delegationData ||
      Object.keys(delegationData).length === 0
    ) {
      return;
    }
    try {
      // Parse dates
      let startDate = null;
      let endDate = null;

      if (delegationData.Start_Date) {
        startDate = new Date(delegationData.Start_Date);
        if (isNaN(startDate.getTime())) startDate = null;
      }

      if (delegationData.End_Date) {
        endDate = new Date(delegationData.End_Date);
        if (isNaN(endDate.getTime())) endDate = null;
      }

      const userId = delegationData.User_ID;
      const altUserId = delegationData.Alternate_User_ID;

      // Find selected accounting officers from options
      const aoOption = accountingOfficerOptions.find(
        (opt) => opt.value?.toString() === userId?.toString(),
      );

      const altAOOption = altAccountingOfficerOptions.find(
        (opt) => opt.value?.toString() === altUserId?.toString(),
      );

      const status = delegationData.Status || "Active";

      const newFormData = {
        ReasonForDelegation:
          delegationData.Comments || delegationData.ReasonForDelegation || "",
        StartDate: startDate,
        EndDate: endDate,
        isConsented:
          delegationData.Has_Consented === "1" ||
          delegationData.Has_Consented === true,
        Accounting_Officer_ID: userId || "",
        Alt_Accounting_Officer_ID: altUserId || "",
        Status: status,
        Remarks: delegationData.Remarks || "",
      };

      setFormData(newFormData);
      setSelectedAO(aoOption || null);
      setSelectedAltAO(altAOOption || null);
    } catch (error) {
      console.error("Error loading delegation data:", error);
    }
  }, [
    show,
    isUpdateMode,
    delegationData,
    accountingOfficerOptions,
    altAccountingOfficerOptions,
  ]);
  
  // Reset submitting state when modal closes
  useEffect(() => {
    if (!show) {
      setIsSubmitting(false);
    }
  }, [show]);
  // Load data when modal opens with delegation data (for update mode)
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Only load when modal is shown and we have delegation data
    if (
      show &&
      isUpdateMode &&
      delegationData &&
      Object.keys(delegationData).length > 0
    ) {
      // Check if delegation data actually changed
      if (
        JSON.stringify(prevDelegationDataRef.current) !==
        JSON.stringify(delegationData)
      ) {
        loadDelegationData();
        prevDelegationDataRef.current = delegationData;
      }
    }
  }, [show, isUpdateMode, delegationData, loadDelegationData]);

  // Set current user as default Accounting Officer for Add mode
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) return;

    if (show && isCreateMode) {
      const currentUserOption = accountingOfficerOptions.find(
        (option) => option.value === user?.User_ID,
      );

      if (currentUserOption && !selectedAO) {
        setSelectedAO(currentUserOption);
        setFormData((prev) => ({
          ...prev,
          Accounting_Officer_ID: currentUserOption.value,
        }));
      }
    }
  }, [show, isCreateMode, accountingOfficerOptions, user, selectedAO]);

  // Reset form when modal closes
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) return;

    if (!show) {
      // Only reset if it was previously shown
      if (prevShowRef.current === true) {
        setFormData(initialFormData);
        setSelectedAO(null);
        setSelectedAltAO(null);
      }
    }
    prevShowRef.current = show;
  }, [show, initialFormData]);

  // Form submission
  const handleFormSubmit = (e) => {
    e.preventDefault();
     // Prevent multiple submissions
    if (isSubmitting || loading) {
      return;
    }
    if (isUpdateMode) {
      const submissionData = {
        Report_Delegation_ID:
          delegationData.Report_Delegation_ID || delegationData.Delegation_ID,
        Status: formData.Status,
        Modified_By: user?.User_Name || user?.User_ID || "System",
        User_ID: parseFloat(
          delegationData.User_ID || delegationData.Accounting_Officer_ID,
        ),
        Alternate_User_ID: parseFloat(
          delegationData.Alternate_User_ID ||
            delegationData.Alt_Accounting_Officer_ID,
        ),
        Comments:
          delegationData.Comments || delegationData.ReasonForDelegation || "",
        Start_Date: delegationData.Start_Date,
        End_Date: delegationData.End_Date,
        Has_Consented: "1",
        Year_ID: Year_ID ? parseFloat(Year_ID) : null,
        Vote_ID: !isLG ? parseFloat(Vote_ID) : 0,
        LG_Vote_ID: isLG ? parseFloat(LG_Vote_ID) : 0,
      };

      if (handleSubmit) {
        handleSubmit(submissionData);
      }
      return;
    }

    if (isCreateMode) {
      const requiredFields = [
        "ReasonForDelegation",
        "StartDate",
        "EndDate",
        "Alt_Accounting_Officer_ID",
      ];

      const missingFields = requiredFields.filter(
        (field) =>
          !formData[field] ||
          (typeof formData[field] === "string" &&
            formData[field].trim() === ""),
      );

      if (missingFields.length > 0) {
        alert("Please fill in all required fields marked with *");
        return;
      }

      if (
        formData.StartDate &&
        formData.EndDate &&
        formData.StartDate > formData.EndDate
      ) {
        alert("End date cannot be earlier than start date");
        return;
      }

      if (!formData.isConsented) {
        alert("You must confirm that consent has been obtained");
        return;
      }

      if (
        formData.Accounting_Officer_ID === formData.Alt_Accounting_Officer_ID
      ) {
        alert(
          "Accounting Officer and Alternative Accounting Officer cannot be the same person",
        );
        return;
      }

      const submissionData = {
        Year_ID: Year_ID ? parseFloat(Year_ID) : null,
        User_ID: parseFloat(formData.Accounting_Officer_ID),
        Alternate_User_ID: parseFloat(formData.Alt_Accounting_Officer_ID),
        Start_Date: formData.StartDate
          ? formData.StartDate.toISOString().split("T")[0]
          : "",
        End_Date: formData.EndDate
          ? formData.EndDate.toISOString().split("T")[0]
          : "",
        Comments: formData.ReasonForDelegation || "",
        Has_Consented: "1",
        Status: "Active",
        Created_By: user?.User_Name || "",
        Modified_By: user?.User_Name || "",
        Vote_ID: !isLG ? parseFloat(Vote_ID) : 0,
        LG_Vote_ID: isLG ? parseFloat(LG_Vote_ID) : 0,
      };

      if (handleSubmit) {
        handleSubmit(submissionData);
      }
    }
  };

  const renderUpdateContent = () => {
    if (!delegationData || Object.keys(delegationData).length === 0) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-3">Loading delegation details...</p>
        </div>
      );
    }

    return (
      <>
        <Row className="mb-4">
          <Col md="12">
            <div className="bg-light p-3 rounded">
              <h6 className="text-primary mb-3">Role Delegation Details</h6>
              <Row>
                <Col md="6">
                  <p>
                    <strong>Accounting Officer:</strong>{" "}
                    {delegationData.Accounting_Officer_Name ||
                      `User ${delegationData.User_ID ||
                        delegationData.Accounting_Officer_ID ||
                        ""}`}
                  </p>
                  <p>
                    <strong>Alternative Officer:</strong>{" "}
                    {delegationData.Alt_Accounting_Officer_Name ||
                      `User ${delegationData.Alternate_User_ID ||
                        delegationData.Alt_Accounting_Officer_ID ||
                        ""}`}
                  </p>
                </Col>
                <Col md="6">
                  <p>
                    <strong>Start Date:</strong>{" "}
                    {delegationData.StartDate_Formatted ||
                      delegationData.Start_Date ||
                      "N/A"}
                  </p>
                  <p>
                    <strong>End Date:</strong>{" "}
                    {delegationData.EndDate_Formatted ||
                      delegationData.End_Date ||
                      "N/A"}
                  </p>
                </Col>
              </Row>
              <p>
                <strong>Reason:</strong>{" "}
                {delegationData.Comments ||
                  delegationData.ReasonForDelegation ||
                  "N/A"}
              </p>
            </div>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col md="12">
            <h6 className="text-primary mb-3">Update Status</h6>
            <Form.Group controlId="Status" className="mb-3">
              <Form.Label>New Status *</Form.Label>
              <Form.Control
                as="select"
                name="Status"
                value={formData.Status}
                onChange={handleChange}
                disabled={loading}
                className="form-control-lg"
                required
              >
                {StatusOptions && StatusOptions.length > 0 ? (
                  StatusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                ) : (
                  <option value="">No status options available</option>
                )}
              </Form.Control>
              <Form.Text className="text-muted">
                Select the new status for this delegation
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>
      </>
    );
  };

  return (
    <Modal
      size="lg"
      show={show}
      onHide={loading ? undefined : onHide}
      aria-labelledby="delegate-modal"
      backdrop={loading ? "static" : true}
      keyboard={!loading}
    >
      <Modal.Header closeButton={!loading}>
        <Modal.Title id="delegate-modal">
          {isCreateMode ? "Create a Role Delegation" : "Update Role Delegation Status"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleFormSubmit}>
        <Modal.Body>
          {isCreateMode ? (
            <>
              <h6 className="text-primary mt-4 mb-3">
                Role Delegation Policy
              </h6>

              <Row className="mt-3">
                <Col md="12">
                  <Form.Group
                    controlId="Alt_Accounting_Officer_ID"
                    className="mb-3"
                  >
                    <Form.Label>Delegate To:*</Form.Label>
                    <ReactSelect
                      name="Alt_Accounting_Officer_ID"
                      value={selectedAltAO}
                      options={altAccountingOfficerOptions}
                      onChange={handleAltAOChange}
                      isLoading={loading}
                      isDisabled={loading}
                      placeholder="Select a User to delegate to"
                      required
                      isSearchable
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />

                    <Form.Text className="text-muted">
                      Select an alternative accounting officer from your vote
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mt-2">
                <Col md="12">
                  <Form.Group controlId="ReasonForDelegation" className="mb-3">
                    <Form.Label>Reason for Delegation: *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      name="ReasonForDelegation"
                      value={formData.ReasonForDelegation}
                      onChange={handleChange}
                      disabled={loading}
                      required
                      placeholder="Please provide a detailed reason for this delegation..."
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col md="6">
                  <Form.Group controlId="StartDate" className="mb-3">
                    <Form.Label>From: *</Form.Label>
                    <DatePicker
                      selected={formData.StartDate}
                      onChange={(date) => handleDateChange(date, "StartDate")}
                      className="form-control"
                      dateFormat="yyyy-MM-dd"
                      minDate={new Date()}
                      isClearable
                      placeholderText="Select start date"
                      required
                      disabled={loading}
                    />
                    <Form.Text className="text-muted">
                      When the delegation should start
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md="6">
                  <Form.Group controlId="EndDate" className="mb-3">
                    <Form.Label>To: *</Form.Label>
                    <DatePicker
                      selected={formData.EndDate}
                      onChange={(date) => handleDateChange(date, "EndDate")}
                      className="form-control"
                      dateFormat="yyyy-MM-dd"
                      minDate={formData.StartDate || new Date()}
                      isClearable
                      placeholderText="Select end date"
                      required
                      disabled={loading}
                    />
                    <Form.Text className="text-muted">
                      When the delegation should end
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mt-3">
                <Col md="12">
                  <Form.Group controlId="isConsented" className="mb-3">
                    <FormCheck
                      type="checkbox"
                      name="isConsented"
                      label="I consent to delegating my role to the selected User"
                      checked={formData.isConsented}
                      onChange={handleChange}
                      disabled={loading}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </>
          ) : (
            renderUpdateContent()
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {isCreateMode ? "Creating..." : "Updating Status..."}
              </>
            ) : isCreateMode ? (
              "Submit Delegation"
            ) : (
              "Update Status"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default React.memo(ReportDelegationModal);
